/**
 * Tooltip: a small dark label for icon controls, shown right away on hover
 * and when the control gets keyboard focus (native `title` tooltips appear
 * only after a long hover, never on focus, and can't be styled).
 *
 * Rendered in a portal with fixed positioning so scrolling or clipped
 * containers (the drawing toolbar scrolls sideways on small screens) never
 * cut it off. The control keeps its own aria-label; the tooltip is visual
 * only, so screen readers don't hear it twice.
 */
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

const HOVER_DELAY_MS = 120;
const GAP = 8;
const EDGE = 8;

export function Tooltip({
  label,
  shortcut,
  children,
  className = '',
}: {
  label: ReactNode;
  /** Keyboard shortcut shown after the label, e.g. "E" or "⌘Z". */
  shortcut?: string;
  children: ReactNode;
  className?: string;
}): JSX.Element {
  const anchorRef = useRef<HTMLSpanElement | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);
  const timer = useRef<number | null>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number; below: boolean } | null>(null);

  function show(delay: number): void {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setOpen(true), delay);
  }
  function hide(): void {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = null;
    setOpen(false);
    setPos(null);
  }

  // Place above the control (below if there's no room), clamped to the viewport.
  useLayoutEffect(() => {
    if (!open || !anchorRef.current || !tipRef.current) return;
    const a = anchorRef.current.getBoundingClientRect();
    const t = tipRef.current.getBoundingClientRect();
    const below = a.top - GAP - t.height < EDGE;
    const top = below ? a.bottom + GAP : a.top - GAP - t.height;
    const centered = a.left + a.width / 2 - t.width / 2;
    const left = Math.min(Math.max(centered, EDGE), window.innerWidth - t.width - EDGE);
    setPos({ left, top, below });
  }, [open]);

  useLayoutEffect(() => () => {
    if (timer.current) window.clearTimeout(timer.current);
  }, []);

  return (
    <span
      ref={anchorRef}
      className={`inline-flex ${className}`}
      onMouseEnter={() => show(HOVER_DELAY_MS)}
      onMouseLeave={hide}
      onFocus={(e) => {
        // Keyboard focus only; a mouse click shouldn't pop a tooltip.
        if ((e.target as HTMLElement).matches(':focus-visible')) show(0);
      }}
      onBlur={hide}
      onPointerDown={hide}
      onKeyDown={(e) => {
        if (e.key === 'Escape') hide();
      }}
    >
      {children}
      {open &&
        createPortal(
          <div
            ref={tipRef}
            aria-hidden="true"
            style={{
              position: 'fixed',
              left: pos?.left ?? -9999,
              top: pos?.top ?? -9999,
              visibility: pos ? 'visible' : 'hidden',
            }}
            className="pointer-events-none z-[1000] max-w-[16rem] rounded-lg bg-ink px-2.5 py-1.5 text-xs font-medium leading-snug text-white shadow-lg"
          >
            <span>{label}</span>
            {shortcut && (
              <kbd className="ml-1.5 rounded border border-white/25 px-1 font-mono text-[10px] font-semibold text-white/80">
                {shortcut}
              </kbd>
            )}
          </div>,
          document.body,
        )}
    </span>
  );
}
